import {
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from "@heroui/react";
import { InvitationFormData } from "./InvitationForm";
import { RenterMetaFormData } from "./RenterMetaForm";
import { RenterRequisiteFormData } from "./RenterRequisiteForm";
import { OfficesSelectionFormData } from "./OfficesSelectionForm";
import { OfficeChip } from "../chips/officeChip";
import { RenterChip } from "../chips/renterChip";
import { RequisiteChip } from "../chips/requisiteChip";
import { RenterRequisiteChip } from "../chips/renterRequisiteChip";
import { FaPlus } from "react-icons/fa6";

interface InvitationReviewProps {
  invitationData: InvitationFormData;
  renterMetaData?: RenterMetaFormData;
  renterRequisiteData?: RenterRequisiteFormData;
  officesData?: OfficesSelectionFormData;
  onEdit: () => void;
  onSubmit: () => void;
  onDelete?: () => void;
  isSubmitting?: boolean;
}

export function InvitationReview({
  invitationData,
  renterMetaData,
  renterRequisiteData,
  officesData,
  onEdit,
  onSubmit,
  onDelete,
  isSubmitting = false,
}: InvitationReviewProps) {
  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("ru-RU");
    } catch {
      return dateString;
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="text-lg font-semibold mb-2">
        Проверьте данные перед отправкой
      </div>

      <div className="flex flex-col gap-4">
        <div className="p-4 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl">
          <h3 className="text-md font-semibold mb-3">Основная информация</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-foreground-500">
                Номер договора:
              </span>
              <div className="mt-1 font-medium">
                {invitationData.number || "н/д"}
              </div>
            </div>
            <div>
              <span className="text-sm text-foreground-500">
                Часы на переговорку:
              </span>
              <div className="mt-1 font-medium">
                {invitationData.meetingHours !== undefined
                  ? invitationData.meetingHours
                  : "н/д"}
              </div>
            </div>
            <div>
              <span className="text-sm text-foreground-500">
                Бизнес-реквизиты:
              </span>
              <div className="mt-1">
                {invitationData.requisite ? (
                  <RequisiteChip requisiteId={invitationData.requisite} />
                ) : (
                  <span className="text-foreground-500">н/д</span>
                )}
              </div>
            </div>
            <div>
              <span className="text-sm text-foreground-500">Дата начала:</span>
              <div className="mt-1 font-medium">
                {invitationData.startDate
                  ? formatDate(invitationData.startDate)
                  : "н/д"}
              </div>
            </div>
            <div>
              <span className="text-sm text-foreground-500">
                Дата окончания:
              </span>
              <div className="mt-1 font-medium">
                {invitationData.endDate
                  ? formatDate(invitationData.endDate)
                  : "н/д"}
              </div>
            </div>
          </div>
        </div>

        {officesData &&
          officesData.offices &&
          officesData.offices.length > 0 && (
            <div className="p-4 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl">
              <h3 className="text-md font-semibold mb-3">Офисы</h3>
              <div className="border rounded-lg overflow-hidden">
                <Table aria-label="Офисы" removeWrapper>
                  <TableHeader>
                    <TableColumn>Офис</TableColumn>
                    <TableColumn>Цена</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {officesData.offices
                      .filter(
                        (item) =>
                          item.office_id !== null && item.office_id !== 0,
                      )
                      .map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {item.office_id ? (
                              <OfficeChip officeId={item.office_id} />
                            ) : (
                              <span className="text-foreground-500">н/д</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">
                              {item.price !== undefined && item.price !== null
                                ? `${item.price} ₽`
                                : "н/д"}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

        {invitationData.type === "existing_renter" && invitationData.renter && (
          <div className="p-4 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl">
            <h3 className="text-md font-semibold mb-3">Арендатор</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-foreground-500">Арендатор:</span>
                <div className="mt-1">
                  <RenterChip renterId={invitationData.renter} />
                </div>
              </div>
              {invitationData.renterRequisite && (
                <div>
                  <span className="text-sm text-foreground-500">
                    Реквизиты арендатора:
                  </span>
                  <div className="mt-1">
                    <RenterRequisiteChip
                      renterRequisiteId={invitationData.renterRequisite}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {invitationData.type === "existing_renter" && renterRequisiteData && (
          <div className="p-4 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl">
            <h3 className="text-md font-semibold mb-3">Реквизиты арендатора</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-foreground-500">
                  Название реквизитов:
                </span>
                <div className="mt-1 font-medium">
                  {renterRequisiteData.requisite_name || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">Юр. адрес:</span>
                <div className="mt-1 font-medium">
                  {renterRequisiteData.address || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">ИНН:</span>
                <div className="mt-1 font-medium">
                  {renterRequisiteData.inn !== undefined
                    ? renterRequisiteData.inn
                    : "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">ОГРН:</span>
                <div className="mt-1 font-medium">
                  {renterRequisiteData.ogrn !== undefined
                    ? renterRequisiteData.ogrn
                    : "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">КПП:</span>
                <div className="mt-1 font-medium">
                  {renterRequisiteData.kpp !== undefined
                    ? renterRequisiteData.kpp
                    : "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">БИК:</span>
                <div className="mt-1 font-medium">
                  {renterRequisiteData.bik !== undefined
                    ? renterRequisiteData.bik
                    : "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">
                  Счёт в банке:
                </span>
                <div className="mt-1 font-medium">
                  {renterRequisiteData.bank_account || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">Корр. счёт:</span>
                <div className="mt-1 font-medium">
                  {renterRequisiteData.correspondent_account || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">
                  Название банка:
                </span>
                <div className="mt-1 font-medium">
                  {renterRequisiteData.bank_name || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">Телефон:</span>
                <div className="mt-1 font-medium">
                  {renterRequisiteData.phone_number || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">Email:</span>
                <div className="mt-1 font-medium">
                  {renterRequisiteData.email || "н/д"}
                </div>
              </div>
            </div>
          </div>
        )}

        {invitationData.type === "new_renter" && renterMetaData && (
          <div className="p-4 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl">
            <h3 className="text-md font-semibold mb-3">
              Информация о новом арендаторе
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-foreground-500">Название:</span>
                <div className="mt-1 font-medium">
                  {renterMetaData.renter_name || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">Юр. адрес:</span>
                <div className="mt-1 font-medium">
                  {renterMetaData.address || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">ИНН:</span>
                <div className="mt-1 font-medium">
                  {renterMetaData.inn !== undefined
                    ? renterMetaData.inn
                    : "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">ОГРН:</span>
                <div className="mt-1 font-medium">
                  {renterMetaData.ogrn !== undefined
                    ? renterMetaData.ogrn
                    : "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">КПП:</span>
                <div className="mt-1 font-medium">
                  {renterMetaData.kpp !== undefined
                    ? renterMetaData.kpp
                    : "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">БИК:</span>
                <div className="mt-1 font-medium">
                  {renterMetaData.bik !== undefined
                    ? renterMetaData.bik
                    : "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">
                  Счёт в банке:
                </span>
                <div className="mt-1 font-medium">
                  {renterMetaData.bank_account || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">Корр. счёт:</span>
                <div className="mt-1 font-medium">
                  {renterMetaData.correspondent_account || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">
                  Название банка:
                </span>
                <div className="mt-1 font-medium">
                  {renterMetaData.bank_name || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">Телефон:</span>
                <div className="mt-1 font-medium">
                  {renterMetaData.phone_number || "н/д"}
                </div>
              </div>
              <div>
                <span className="text-sm text-foreground-500">Email:</span>
                <div className="mt-1 font-medium">
                  {renterMetaData.email || "н/д"}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 justify-end mt-4">
        {onDelete && (
          <Button
            color="danger"
            variant="light"
            onPress={onDelete}
            isDisabled={isSubmitting}>
            Удалить
          </Button>
        )}
        <Button
          color="default"
          variant="bordered"
          onPress={onEdit}
          isDisabled={isSubmitting}>
          Исправить
        </Button>
        <Button
          color="primary"
          onPress={onSubmit}
          isLoading={isSubmitting}
          isDisabled={isSubmitting}
          startContent={<FaPlus />}>
          Сохранить
        </Button>
      </div>
    </div>
  );
}
